import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  A4, SHEET_PRESETS, LABEL_FIELDS, defaultTemplate, fieldDef,
  labelPositions, sheetProblems, perSheet,
} from '../../utils/labelSheet';

// Label setup: the sheet's measurements and what one sticker prints.
//
// The template is saved per event. The API is tried first so every admin of the
// event prints the same thing; when the endpoint is not there yet (an older
// server) it falls back to this browser, so the page always works.

const KEY = (eventId) => `se_label_template:${eventId}`;

export const loadTemplate = (eventId) => {
  try {
    const raw = localStorage.getItem(KEY(eventId));
    if (!raw) return defaultTemplate();
    const saved = JSON.parse(raw);
    return { ...defaultTemplate(), ...saved, sheet: { ...defaultTemplate().sheet, ...(saved.sheet || {}) } };
  } catch { return defaultTemplate(); }
};
const saveLocal = (eventId, tpl) => {
  try { localStorage.setItem(KEY(eventId), JSON.stringify(tpl)); } catch { /* private mode */ }
};

export function useLabelSetup(event) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [tpl, setTpl] = useState(() => loadTemplate(event?.EventId));
  const [picked, setPicked] = useState(tpl.blocks[0]?.id || '');
  const [zoom, setZoom] = useState(6);          // px per mm in the designer
  const dragRef = useRef(null);

  useEffect(() => { setTpl(loadTemplate(event?.EventId)); }, [event]);

  // Every change is kept at once: a label template is fiddled with, not filled
  // in, and losing the fiddling to a reload would be maddening.
  const change = useCallback((patch) => {
    setTpl(t => {
      const next = typeof patch === 'function' ? patch(t) : { ...t, ...patch };
      saveLocal(event?.EventId, next);
      return next;
    });
  }, [event]);

  const setSheet = (key, value) => change(t => ({ ...t, sheet: { ...t.sheet, [key]: Number(value) || 0 } }));

  const usePreset = (key) => {
    const preset = SHEET_PRESETS.find(p => p.key === key);
    if (!preset) return;
    change(t => ({ ...t, preset: key, sheet: { ...preset.sheet } }));
  };

  // ── The blocks on one label ──────────────────────────────────────────────
  const block = tpl.blocks.find(b => b.id === picked) || null;
  const setBlock = (patch) => change(t => ({
    ...t,
    blocks: t.blocks.map(b => (b.id === picked ? { ...b, ...patch } : b)),
  }));
  const addBlock = (field) => {
    const id = `b${Date.now().toString(36)}`;
    change(t => ({
      ...t,
      blocks: [...t.blocks, {
        id, field, x: 3, y: Math.min(3 + t.blocks.length * 5, Math.max(t.sheet.labelHeight - 6, 3)),
        w: Math.max(t.sheet.labelWidth - 6, 10), size: 9, bold: false, align: 'left',
        text: field === 'text' ? 'With best compliments' : undefined,
      }],
    }));
    setPicked(id);
  };
  const removeBlock = (id) => {
    change(t => ({ ...t, blocks: t.blocks.filter(b => b.id !== id) }));
    if (picked === id) setPicked('');
  };

  // Dragging inside the label: the designer reports mm, clamped to the sticker.
  const startDrag = (id, e, mmPerPx) => {
    const b = tpl.blocks.find(x => x.id === id);
    if (!b) return;
    setPicked(id);
    dragRef.current = { id, startX: e.clientX, startY: e.clientY, x: b.x, y: b.y, mmPerPx };
  };
  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (e.clientX - d.startX) * d.mmPerPx;
      const dy = (e.clientY - d.startY) * d.mmPerPx;
      change(t => ({
        ...t,
        blocks: t.blocks.map(b => (b.id === d.id ? {
          ...b,
          // Snap to the millimetre and keep the block on the sticker.
          x: Math.max(0, Math.min(Math.round(d.x + dx), Math.max(t.sheet.labelWidth - 2, 0))),
          y: Math.max(0, Math.min(Math.round(d.y + dy), Math.max(t.sheet.labelHeight - 2, 0))),
        } : b)),
      }));
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [change]);

  // Arrow keys nudge the chosen block by a millimetre (Shift = 5).
  const nudge = (dx, dy) => setBlock({
    x: Math.max(0, Math.round((block?.x || 0) + dx)),
    y: Math.max(0, Math.round((block?.y || 0) + dy)),
  });

  const problems = useMemo(() => sheetProblems(tpl.sheet), [tpl.sheet]);
  const positions = useMemo(() => labelPositions(tpl.sheet, tpl.offsetX, tpl.offsetY), [tpl.sheet, tpl.offsetX, tpl.offsetY]);

  // A test page: the grid as the printer will put it down, each sticker
  // numbered, so a drift can be measured with a ruler and typed into offset.
  const printTest = async () => {
    try {
      const { default: JsPDF } = await import('jspdf');
      const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      doc.setFontSize(8);
      positions.forEach((p, i) => {
        doc.setDrawColor(150); doc.setLineWidth(0.1);
        doc.rect(p.x, p.y, tpl.sheet.labelWidth, tpl.sheet.labelHeight);
        doc.setTextColor(120);
        doc.text(String(i + 1), p.x + 1.5, p.y + 4);
        doc.setDrawColor(210);
        doc.line(p.x, p.y + tpl.sheet.labelHeight / 2, p.x + tpl.sheet.labelWidth, p.y + tpl.sheet.labelHeight / 2);
        doc.line(p.x + tpl.sheet.labelWidth / 2, p.y, p.x + tpl.sheet.labelWidth / 2, p.y + tpl.sheet.labelHeight);
      });
      doc.setTextColor(20);
      doc.text('Test sheet — print at 100% (no "fit to page"), lay a real sheet on top and read the drift in mm', 8, 6);
      const url = URL.createObjectURL(doc.output('blob'));
      window.open(url, 'mehfil-label-test');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast('Could not build the test sheet'); }
  };

  const reset = () => { if (window.confirm('Start again from the standard template?')) change(defaultTemplate()); };

  return {
    isAdmin, event, tpl, change, setSheet, usePreset, presets: SHEET_PRESETS,
    fields: LABEL_FIELDS, fieldDef, block, picked, setPicked, setBlock, addBlock, removeBlock,
    startDrag, nudge, zoom, setZoom, problems, positions, perSheet: perSheet(tpl.sheet), A4,
    printTest, reset,
  };
}
